import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import { ModalCloseReason, ModalRef } from '@tdwb/core';
import { Agreement, AgreementsService, AgreementStatus, AgreementType } from '@tdwb/lazy/platform/preferences';
import { AmexAgreementComponent, AmexAgreementFormModel } from './amex-agreement.component';
import { CegAgreementComponent } from './ceg-agreement.component';
import { MeAgreementComponent } from './me-agreement.component';
import { NasdaqAgreementComponent, NasdaqAgreementFormModel } from './nasdaq-agreement.component';
import { OpraAgreementComponent, OpraAgreementFormModel } from './opra-agreement.component';
import { TdAgreementComponent } from './td-agreement.component';
import { NyseAgreementComponent, NyseAgreementFormModel } from './nyse-agreement.component';

@Component({
  selector: 'td-wb-current-agreements-modal',
  templateUrl: 'current-agreements.modal.component.html',
  styleUrls: ['current-agreements.modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
  // providers: [PAGE_SCROLL_TOP_STRATEGY_PROVIDER] TODO: look into why this throws error
  ,
  standalone: false
})
export class CurrentAgreementsModalComponent implements OnInit {
  @Input() agreements: Array<Agreement> = [];
  @Input() pagingMode: boolean = false;
  @Input() itemsIndex: number = 0;

  @ViewChild(CegAgreementComponent) ceg: CegAgreementComponent;
  @ViewChild(MeAgreementComponent) me: MeAgreementComponent;
  @ViewChild(TdAgreementComponent) td: TdAgreementComponent;
  @ViewChild(AmexAgreementComponent) amex: AmexAgreementComponent;
  @ViewChild(NasdaqAgreementComponent) nasdaq: NasdaqAgreementComponent;
  @ViewChild(NyseAgreementComponent) nyse: NyseAgreementComponent;
  @ViewChild(OpraAgreementComponent) opra: OpraAgreementComponent;

  currentAgreement: Agreement;
  itemsCount: number = 0;
  pendingCount: number = 0;

  constructor(private agreementsService: AgreementsService,
              private changeDetectorRef: ChangeDetectorRef,
              private modalRef: ModalRef<CurrentAgreementsModalComponent>) {
  }

  ngOnInit(): void {
    if (this.itemsIndex === undefined) {
      this.itemsIndex = 0;
    }

    if (this.agreements.length === 0) {
      this.close();
    }
    // when paging mode , cycle through
    this.currentAgreement = this.agreements[this.itemsIndex];
    this.itemsCount = this.agreements.length;
    this.updatePendingCount();
  }

  public declineAgreement(): void {
    this.proceedToNextAgreement();
  }

  public acceptAgreement(): void {
    let model: boolean | NasdaqAgreementFormModel | AmexAgreementFormModel | NyseAgreementFormModel | OpraAgreementFormModel;
    switch (this.currentAgreement.agreementType) {
      case AgreementType.AMEX:
        model = this.amex.trySubmitAndReturnModel();
        break;
      case AgreementType.NASDAQ:
        model = this.nasdaq.trySubmitAndReturnModel();
        break;
      case AgreementType.NYSE:
        model = this.nyse.trySubmitAndReturnModel();
        break;
      case AgreementType.OPRA:
        model = this.opra.trySubmitAndReturnModel();
        break;
      default:
        model = undefined;
        break;
    }
    if (model !== undefined) {
      this.currentAgreement.agreementStatus = AgreementStatus.ACCEPTED;
      this.currentAgreement.exchangeSubscriptionData = model;
      this.agreementsService.updateAgreement(this.currentAgreement).subscribe((): void => {
        this.proceedToNextAgreement();
      });
    }
  }

  public isDisabled(): boolean {
    return this.currentAgreement.agreementStatus === 'ACCEPTED';
  }

  public close(): void {
    this.modalRef.close(ModalCloseReason.HEADER_BUTTON);
  }

  pageChange(index: number): void {
    this.itemsIndex = index;
    this.currentAgreement = this.agreements[this.itemsIndex];
    this.updatePendingCount();
  }

  private updatePendingCount(): void {
    this.pendingCount = this.agreements.reduce((initialValue: number, agreement: Agreement): number => {
      const unSignedAgreement = agreement.agreementStatus === 'UNSIGNED';
      return unSignedAgreement ? initialValue + 1 : initialValue;
    }, 0);
  }

  private proceedToNextAgreement(): void {
    if (this.pagingMode) {
      this.pendingCount--;
      if (this.pendingCount > 0) {
        this.itemsIndex++;
      }

      if (this.pendingCount > 0) {
        this.currentAgreement = this.agreements[this.itemsIndex];
        this.changeDetectorRef.markForCheck();
      }
      if (this.pendingCount === 0) {
        this.close();
      }
    } else {
      this.close();
    }
  }
}
